import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { authService } from '../services/authService';
import * as Sentry from "@sentry/react";

const ResetPasswordEmail = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [tokenValid, setTokenValid] = useState(false);

    useEffect(() => {
        const token = searchParams.get('token');
        if (!token) {
            setError('Invalid reset link');
            return;
        }

        const verifyToken = async () => {
            try {
                const response = await authService.verifyResetToken(token);
                if (response.valid) {
                    setEmail(response.email);
                    setTokenValid(true);
                } else {
                    setError('Invalid or expired reset link');
                }
            } catch (err: any) {
                Sentry.captureException(err);
                console.error('Reset password token verification error:', err);
                setError(
                  import.meta.env.DEV
                    ? err.message || 'Failed to verify reset link'
                    : 'Unable to verify reset link. Please try again later.'
                );
            }
        };

        verifyToken();
    }, [searchParams]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        try {
            const token = searchParams.get('token');
            if (!token) {
                throw new Error('Invalid reset link');
            }

            await authService.resetPassword(token, newPassword);
            
            setSuccess(true);
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err: any) {
            Sentry.captureException(err);
            console.error('Reset password error:', err);
            setError(
              import.meta.env.DEV
                ? err.message || 'Failed to reset password'
                : 'Unable to reset password. Please try again later.'
            );
        } finally {
            setLoading(false);
        }
    };

    if (!tokenValid && error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
                <div className="w-full max-w-md">
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl mb-4">
                            <img src="/apple-touch-icon.png" alt="CareLMS Logo" width={64} height={64} className="rounded-xl" />
                        </div>
                        <h1 className="text-3xl font-bold text-white mb-2">CareLMS</h1>
                    </div>

                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <div className="text-center mb-6">
                            <h2 className="text-xl font-semibold text-black mb-2">Invalid Reset Link</h2>
                        </div>
                        
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
                            <p className="text-red-600 text-sm">{error}</p>
                        </div>
                        
                        <button
                            onClick={() => navigate('/login')}
                            className="w-full flex items-center justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                        >
                            Return to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl mb-4">
                        <img src="/apple-touch-icon.png" alt="CareLMS Logo" width={64} height={64} className="rounded-xl" />
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-2">CareLMS</h1>
                    <p className="text-blue-100">Set your new password</p>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="text-center mb-6">
                        <h2 className="text-xl font-semibold text-black mb-2">Reset Password</h2>
                        <p className="text-slate-600 text-sm">Enter your new password below</p>
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-red-600 text-sm">{error}</p>
                        </div>
                    )}

                    {success && (
                        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-green-600 text-sm">Password reset successful! Redirecting to login...</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                                Email Address
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail className="h-4 w-4 text-slate-400" />
                                </div>
                                <input
                                    id="email"
                                    type="email"
                                    value={email}
                                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg text-slate-900 bg-slate-50 cursor-not-allowed"
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="newPassword" className="block text-sm font-medium text-slate-700">
                                New Password
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock className="h-4 w-4 text-slate-400" />
                                </div>
                                <input
                                    id="newPassword"
                                    type={showNewPassword ? "text" : "password"}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="w-full pl-10 pr-12 py-3 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                                    placeholder="Enter new password"
                                    required
                                    disabled={success}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                                >
                                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700">
                                Confirm New Password
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock className="h-4 w-4 text-slate-400" />
                                </div>
                                <input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="w-full pl-10 pr-12 py-3 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                                    placeholder="Confirm new password"
                                    required
                                    disabled={success}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                                >
                                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading || success}
                            className={`w-full flex items-center justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 ${
                                (loading || success) ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                        >
                            {loading ? 'Resetting...' : success ? 'Success!' : 'Reset Password'}
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </button>
                    </form>
                </div>

                <div className="text-center mt-6">
                    <p className="text-blue-100 text-sm">Built for Care. Trusted by Care Businesses.</p>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordEmail; 